import React from 'react';
import Table from "src/componets/Table";
import { useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { RefundInterface } from "src/store/callrecorder/refundsSlice";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";
import callrecorderUrls from "src/configs/callrecorder/callrecorderUrls";

import { Tag, Tooltip } from "antd";

interface ListTableInterface {
  className?: string;
  limit: number;
}

const _ListTable = ({ className, limit }: ListTableInterface) => {
  const ids = useAppSelector(state => state.callrecorder.refunds.ids);
  const entities = useAppSelector(state => state.callrecorder.refunds.entities);
  const count = useAppSelector(state => state.callrecorder.refunds.count);
  const data = ids ? ids.map(id => entities[id]) : [];

  const platformColor = (platform: string | null) => {
    switch (platform) {
      case 'ios': return 'blue';
      case 'android': return 'green';
      default: return 'default';
    }
  };

  // Fraud and chargebacks are the ones worth spotting at a glance.
  const reasonColor = (reason: string | null) => {
    switch (reason) {
      case 'chargeback':
      case 'fraud':
      case 'friendly_fraud': return 'error';
      case 'app_issue':
      case 'defective':
      case 'not_received': return 'warning';
      case 'remorse':
      case 'accidental_purchase': return 'orange';
      default: return 'default';
    }
  };

  const columns: ColumnsType<RefundInterface> = [
    {
      key: 'id',
      title: 'Order',
      render: (_, { id }) => <small>{`#${id}`}</small>,
      width: 70
    },

    {
      key: 'user_id',
      title: 'User',
      render: (_, { user_id }) => user_id ? (
        <OptionalLink link={callrecorderUrls.userPath(user_id)}>
          <small>{`#${user_id}`}</small>
        </OptionalLink>
      ) : '—',
      width: 60
    },

    {
      key: 'platform',
      title: 'Platform',
      render: (_, { platform }) => platform ? <Tag color={platformColor(platform)}>{platform}</Tag> : '—',
      width: 90
    },

    {
      key: 'productid',
      title: 'Product ID',
      render: (_, { productid }) => productid ? <small>{productid}</small> : '—',
      width: 150
    },

    {
      key: 'amount',
      title: 'Coins',
      render: (_, { amount }) => amount ? <small>{amount}</small> : '—',
      width: 70
    },

    {
      key: 'price',
      title: 'Price',
      render: (_, { price }) => price ? <small>{price}</small> : '—',
      width: 70
    },

    {
      key: 'refund_reason',
      title: 'Reason',
      render: (_, { refund_reason }) => refund_reason
        ? <Tag color={reasonColor(refund_reason)}>{refund_reason}</Tag>
        : '—',
      width: 130
    },

    {
      key: 'refund_source',
      title: 'Source',
      render: (_, { refund_source }) => refund_source ? <Tag>{refund_source}</Tag> : '—',
      width: 90
    },

    {
      key: 'transaction_id',
      title: 'Transaction',
      render: (_, { transaction_id }) => transaction_id ? (
        <Tooltip title={transaction_id}>
          <small>{transaction_id.length > 20 ? `${transaction_id.slice(0, 20)}…` : transaction_id}</small>
        </Tooltip>
      ) : '—',
      width: 150
    },

    {
      key: 'refunded_at',
      title: 'Refunded',
      render: (_, { refunded_at }) => refunded_at ? <small>{formattedDate(refunded_at).dateTime}</small> : '—',
      width: 125
    },

    {
      key: 'created_at',
      title: 'Purchased',
      render: (_, { created_at }) => <small>{formattedDate(created_at).dateTime}</small>,
      width: 125
    },
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        sort_keys={['id', 'user_id', 'amount', 'price', 'refunded_at', 'created_at']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
